import type { ComponentType, SVGProps } from "react"

import * as m from "@/paraglide/messages"
import LuniBoardBg from "@/shared/assets/board/bg-circle-blur.svg?react"
import LuniBgGradient from "@/shared/assets/characters/luni/bg-gradient-blur.svg?react"
import LuniCardBg from "@/shared/assets/characters/luni/card-bg.svg?react"
import LuniEarLeft from "@/shared/assets/characters/luni/ear-left.svg?react"
import LuniEarRight from "@/shared/assets/characters/luni/ear-right.svg?react"
import LuniBubbleEars from "@/shared/assets/characters/luni/ears.svg?react"
import MarsBoardBg from "@/shared/assets/characters/mars/bg-circle-blur.svg?react"
import MarsBgGradient from "@/shared/assets/characters/mars/bg-gradient-blur.svg?react"
import MarsCardBg from "@/shared/assets/characters/mars/card-bg.svg?react"
import MarsEarLeft from "@/shared/assets/characters/mars/ear-left.svg?react"
import MarsEarRight from "@/shared/assets/characters/mars/ear-right.svg?react"
import MarsBubbleHorns from "@/shared/assets/characters/mars/horns.svg?react"
import SolaBoardBg from "@/shared/assets/characters/sola/bg-circle-blur.svg?react"
import SolaBgGradient from "@/shared/assets/characters/sola/bg-gradient-blur.svg?react"
import SolaCardBg from "@/shared/assets/characters/sola/card-bg.svg?react"
import SolaEarLeft from "@/shared/assets/characters/sola/ear-left.svg?react"
import SolaEarRight from "@/shared/assets/characters/sola/ear-right.svg?react"
import SolaBubbleEars from "@/shared/assets/characters/sola/ears.svg?react"

import type { CharacterType } from "../model/types"

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>

export interface CharacterConfig {
	name: () => string
	defaultGreeting: () => string
	// Header
	nameColor: string
	dotColor: string
	// Message
	messageColor: string
	// Tab-bar card background
	cardBg: SvgComponent
	// Tab-bar background gradient (bottom slider area on home)
	bgGradient: SvgComponent
	// Board page full-screen background (circle-blur gradient)
	boardBg: SvgComponent
	// Speech-bubble ears/horns on top of incoming message bubble (board page)
	bubbleEars: SvgComponent
	decorations: {
		component: SvgComponent
		className: string
	}[]
}

const charactersConfig: Record<CharacterType, CharacterConfig> = {
	luni: {
		name: m.characterNameLuni,
		defaultGreeting: m.characterGreetingLuni,
		nameColor: "#373b47",
		dotColor: "#13C4D1",
		messageColor: "#5d6479",
		cardBg: LuniCardBg,
		bgGradient: LuniBgGradient,
		boardBg: LuniBoardBg,
		bubbleEars: LuniBubbleEars,
		decorations: [
			{ component: LuniEarLeft, className: "absolute -top-4 left-6 -z-20" },
			{ component: LuniEarRight, className: "absolute -top-4 left-16 -z-20" },
		],
	},
	sola: {
		name: m.characterNameSola,
		defaultGreeting: m.characterGreetingSola,
		nameColor: "#373b47",
		dotColor: "#00d0ff",
		messageColor: "#5d6479",
		cardBg: SolaCardBg,
		bgGradient: SolaBgGradient,
		boardBg: SolaBoardBg,
		bubbleEars: SolaBubbleEars,
		decorations: [
			{ component: SolaEarLeft, className: "absolute -top-4 left-6 -z-20" },
			{ component: SolaEarRight, className: "absolute -top-4 left-16 -z-20" },
		],
	},
	mars: {
		name: m.characterNameMars,
		defaultGreeting: m.characterGreetingMars,
		nameColor: "white",
		dotColor: "#00d0ff",
		messageColor: "white",
		cardBg: MarsCardBg,
		bgGradient: MarsBgGradient,
		boardBg: MarsBoardBg,
		bubbleEars: MarsBubbleHorns,
		decorations: [
			{ component: MarsEarLeft, className: "absolute -top-4 left-6 -z-20" },
			{ component: MarsEarRight, className: "absolute -top-4 left-16 -z-20" },
		],
	},
}

export const CHARACTERS: readonly CharacterType[] = ["luni", "sola", "mars"]

export function getCharacterConfig(character: CharacterType): CharacterConfig {
	return charactersConfig[character]
}
